import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { BreadcrumbItem } from '@/components/PageBreadcrumbs.vue'
import type { ProductById } from '@/gql/reviews'
import { ReviewsInsightsPerspective, ReviewsRouteName } from '@/router/types'

import { buildBreadcrumb } from './buildBreadcrumb'

export const useReviewsProductBreadcrumbs = (
  product: Pick<ProductById, 'id' | 'title'>,
  campaignName?: Ref<string>,
  leadName?: Ref<string>,
): Ref<BreadcrumbItem[]> => {
  const route = useRoute()
  const { t } = useI18n()

  const params = { productId: product.id }
  const productName = product.title

  return computed(() => {
    const reviewsRoot = buildBreadcrumb(route, { name: ReviewsRouteName.ReviewsProducts }, t('breadcrumbs.reviews.root'))

    const reviewsProductViewReviews = buildBreadcrumb(
      route,
      {
        name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns,
        params: { productId: product.id },
      },
      t('breadcrumbs.reviews.product.view.reviews', { productName: product.title }),
    )

    const reviewsProductViewDetails = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductViewDetails, params },
      t('breadcrumbs.reviews.product.view.details', { productName }),
    )

    const reviewsProductEditDetails = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductEditDetails, params },
      t('breadcrumbs.reviews.product.edit.details', { productName }),
    )

    const reviewsProductViewGlobalFeatures = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductViewGlobalFeatures, params },
      t('breadcrumbs.reviews.product.view.globalFeatures', { productName }),
    )

    const reviewsProductEditGlobalFeatures = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductEditGlobalFeatures, params },
      t('breadcrumbs.reviews.product.edit.globalFeatures', { productName }),
    )

    const reviewsProductViewPricing = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductViewPricing, params },
      t('breadcrumbs.reviews.product.view.pricing', { productName }),
    )

    const reviewsProductEditPricing = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductEditPricing, params },
      t('breadcrumbs.reviews.product.edit.pricing', { productName }),
    )

    const reviewsProductViewAssets = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductViewAssets, params },
      t('breadcrumbs.reviews.product.view.assets', { productName }),
    )

    const reviewsProductEditAssets = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductEditAssets, params },
      t('breadcrumbs.reviews.product.edit.assets', { productName }),
    )

    const reviewsProductViewPreferences = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductPreferencesView, params },
      t('breadcrumbs.reviews.product.view.preferences', { productName }),
    )

    const reviewsProductEditPreferences = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductPreferencesEdit, params },
      t('breadcrumbs.reviews.product.edit.preferences', { productName }),
    )

    const reviewsProductViewResources = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductResourcesView, params },
      t('breadcrumbs.reviews.product.view.media', { productName }),
    )

    const reviewsProductViewResourcesWidgets = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductResourcesWidgetsView, params },
      t('breadcrumbs.reviews.product.view.media', { productName }),
    )

    const reviewsProductViewResourcesBadges = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductResourcesBadgesView, params },
      t('breadcrumbs.reviews.product.view.badges', { productName }),
    )

    const reviewsProductViewLeads = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductLeads, params },
      t('breadcrumbs.reviews.product.view.leads', { productName }),
    )

    const reviewsProductViewTopProductsRanking = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductTopProductsRanking, params },
      t('breadcrumbs.reviews.product.view.top100Ranking', { productName }),
    )

    const reviewsProductLeadDetail = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductLeadDetail, params },
      leadName?.value || '',
    )

    const reviewsProductViewCampaignNew = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductCampaignNew, params },
      t('breadcrumbs.reviews.product.view.campaignNew'),
    )

    const reviewsProductViewCampaignOverview = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductCampaignOverview, params },
      t('breadcrumbs.reviews.product.view.campaignOverview', { campaignName: campaignName?.value || '' }),
    )

    const reviewsProductEditCampaign = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductCampaignEdit, params },
      t('breadcrumbs.reviews.product.edit.campaign'),
    )

    const reviewsProductViewCampaignPersonalizationNew = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductCampaignPersonalizationNew, params },
      t('breadcrumbs.reviews.product.view.campaignPersonalisationNew'),
    )

    const reviewsProductViewCampaignPersonalizationEdit = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductCampaignPersonalizationEdit, params },
      t('breadcrumbs.reviews.product.view.campaignPersonalizationEdit'),
    )

    const insightsProduct = buildBreadcrumb(route, { name: ReviewsRouteName.ReviewsProductView, params }, productName)
    const insightsHub = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsProductInsights, params },
      t('breadcrumbs.reviews.product.view.insights'),
    )
    const insightsTab = (name: ReviewsRouteName, perspective: ReviewsInsightsPerspective) =>
      buildBreadcrumb(route, { name, params }, t(`Reviews.product.insights.perspectives.${perspective}`))

    const breadcrumbsMap: Partial<Record<ReviewsRouteName, BreadcrumbItem[]>> = {
      [ReviewsRouteName.ReviewsProductView]: [],
      [ReviewsRouteName.ReviewsProductEdit]: [],
      [ReviewsRouteName.ReviewsProductsIndex]: [reviewsRoot, reviewsProductViewReviews],
      [ReviewsRouteName.ReviewsProductViewDetails]: [reviewsRoot, reviewsProductViewDetails],
      [ReviewsRouteName.ReviewsProductEditDetails]: [reviewsRoot, reviewsProductViewDetails, reviewsProductEditDetails],
      [ReviewsRouteName.ReviewsProductViewGlobalFeatures]: [reviewsRoot, reviewsProductViewGlobalFeatures],
      [ReviewsRouteName.ReviewsProductEditGlobalFeatures]: [
        reviewsRoot,

        reviewsProductViewGlobalFeatures,
        reviewsProductEditGlobalFeatures,
      ],
      [ReviewsRouteName.ReviewsProductViewPricing]: [reviewsRoot, reviewsProductViewPricing],
      [ReviewsRouteName.ReviewsProductEditPricing]: [reviewsRoot, reviewsProductViewPricing, reviewsProductEditPricing],
      [ReviewsRouteName.ReviewsProductViewAssets]: [reviewsRoot, reviewsProductViewAssets],
      [ReviewsRouteName.ReviewsProductEditAssets]: [reviewsRoot, reviewsProductViewAssets, reviewsProductEditAssets],
      [ReviewsRouteName.ReviewsProductPreferencesView]: [reviewsRoot, reviewsProductViewPreferences],
      [ReviewsRouteName.ReviewsProductPreferencesEdit]: [reviewsRoot, reviewsProductViewPreferences, reviewsProductEditPreferences],
      [ReviewsRouteName.ReviewsProductReviewsAndCampaigns]: [reviewsRoot, reviewsProductViewReviews],
      [ReviewsRouteName.ReviewsProductResourcesView]: [reviewsRoot, reviewsProductViewResources],
      [ReviewsRouteName.ReviewsProductResourcesWidgetsView]: [reviewsRoot, reviewsProductViewResourcesWidgets],
      [ReviewsRouteName.ReviewsProductResourcesBadgesView]: [reviewsRoot, reviewsProductViewResourcesBadges],
      [ReviewsRouteName.ReviewsProductLeads]: [reviewsRoot, reviewsProductViewLeads],
      [ReviewsRouteName.ReviewsProductTopProductsRanking]: [reviewsRoot, reviewsProductViewTopProductsRanking],
      [ReviewsRouteName.ReviewsProductInsightsMarket]: [
        insightsProduct,
        insightsHub,
        insightsTab(ReviewsRouteName.ReviewsProductInsightsMarket, ReviewsInsightsPerspective.Market),
      ],
      [ReviewsRouteName.ReviewsProductInsightsCompetition]: [
        insightsProduct,
        insightsHub,
        insightsTab(ReviewsRouteName.ReviewsProductInsightsCompetition, ReviewsInsightsPerspective.Competition),
      ],
      [ReviewsRouteName.ReviewsProductInsightsBuyer]: [
        insightsProduct,
        insightsHub,
        insightsTab(ReviewsRouteName.ReviewsProductInsightsBuyer, ReviewsInsightsPerspective.Buyer),
      ],
      [ReviewsRouteName.ReviewsProductInsightsReviews]: [
        insightsProduct,
        insightsHub,
        insightsTab(ReviewsRouteName.ReviewsProductInsightsReviews, ReviewsInsightsPerspective.Reviews),
      ],
      [ReviewsRouteName.ReviewsProductLeadDetail]: [reviewsRoot, reviewsProductViewLeads, reviewsProductLeadDetail],
      [ReviewsRouteName.ReviewsProductCampaignNew]: [reviewsRoot, reviewsProductViewReviews, reviewsProductViewCampaignNew],
      [ReviewsRouteName.ReviewsProductCampaignOverview]: [reviewsRoot, reviewsProductViewReviews, reviewsProductViewCampaignOverview],
      [ReviewsRouteName.ReviewsProductCampaignEdit]: [
        reviewsRoot,
        reviewsProductViewReviews,
        reviewsProductViewCampaignOverview,
        reviewsProductEditCampaign,
      ],
      [ReviewsRouteName.ReviewsProductCampaignPersonalizationNew]: [
        reviewsRoot,
        reviewsProductViewReviews,
        reviewsProductViewCampaignPersonalizationNew,
      ],
      [ReviewsRouteName.ReviewsProductCampaignPersonalizationEdit]: [
        reviewsRoot,
        reviewsProductViewReviews,
        reviewsProductViewCampaignOverview,
        reviewsProductViewCampaignPersonalizationEdit,
      ],
    }

    return breadcrumbsMap[route.name as ReviewsRouteName] || []
  })
}
